import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import type { CognitoUser } from 'amazon-cognito-identity-js'

function getCognitoErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const cognitoError = err as { code: string; message?: string }
    switch (cognitoError.code) {
      case 'NotAuthorizedException':
        return 'Incorrect username or password'
      case 'UserNotFoundException':
        return 'User not found'
      case 'UserNotConfirmedException':
        return 'Account not verified. Please check your email.'
      case 'PasswordResetRequiredException':
        return 'Password reset required. Please contact administrator.'
      case 'TooManyRequestsException':
        return 'Too many attempts. Please try again later.'
      case 'NetworkError':
        return 'Network error. Please check your connection.'
      case 'InvalidPasswordException':
        return cognitoError.message || 'Password does not meet requirements'
      case 'InvalidParameterException':
        return cognitoError.message || 'Invalid input provided'
      default:
        return cognitoError.message || 'Authentication failed'
    }
  }
  return err instanceof Error ? err.message : 'Authentication failed'
}

interface PasswordChallenge {
  cognitoUser: CognitoUser
  username: string
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    loginWithCognito,
    completeNewPassword,
  } = useAuth()

  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  // New password challenge state
  const [passwordChallenge, setPasswordChallenge] = useState<PasswordChallenge | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    if (!username.trim() || !password) {
      setError('Username and password are required')
      setIsLoading(false)
      return
    }

    try {
      const result = await loginWithCognito(username.trim(), password)
      if (result.type === 'newPasswordRequired') {
        setPasswordChallenge({
          cognitoUser: result.cognitoUser,
          username: result.username,
        })
        setPassword('')
      } else {
        navigate(from, { replace: true })
      }
    } catch (err) {
      setError(getCognitoErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    if (!newPassword) {
      setError('New password is required')
      setIsLoading(false)
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    if (!passwordChallenge) {
      setError('Session expired. Please try logging in again.')
      setIsLoading(false)
      return
    }

    try {
      await completeNewPassword(passwordChallenge.cognitoUser, newPassword)
      navigate(from, { replace: true })
    } catch (err) {
      setError(getCognitoErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setPasswordChallenge(null)
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--spacing-lg)',
    }}>
      <div className="card" style={{ width: '100%', maxWidth: '400px' }}>
        <h1 style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
          Violet Research
        </h1>

        {error && (
          <div className="error" style={{ marginBottom: 'var(--spacing-md)' }}>
            {error}
          </div>
        )}

        {passwordChallenge ? (
          <form onSubmit={handleNewPasswordSubmit}>
            <p style={{
              marginBottom: 'var(--spacing-md)',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}>
              Please set a new password for your account.
            </p>

            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label htmlFor="new-password">New Password</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
              <label htmlFor="confirm-password">Confirm Password</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <button
              className="btn btn-primary"
              type="submit"
              style={{ width: '100%', marginBottom: 'var(--spacing-sm)' }}
              disabled={isLoading}
            >
              {isLoading ? 'Setting password...' : 'Set Password'}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%' }}
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              Back to Login
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                disabled={isLoading}
                autoComplete="username"
              />
            </div>

            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>

            <button
              className="btn btn-primary"
              type="submit"
              style={{ width: '100%' }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
